import { IntakeAnswer } from "../types/interviewSession";

export type RubricTrait = "accountability" | "coachability";

export type StepRubric = {
  trait: RubricTrait;
  green: RegExp[];
  red: RegExp[];
};

export type RubricScores = {
  accountability: number;
  coachability: number;
  flags: string[];
};

/** Internal answer-pattern signals from Agentic intake PDF (not user-facing coaching). */
export const STEP_RUBRICS: Partial<Record<string, StepRubric>> = {
  q3: {
    trait: "accountability",
    green: [
      /\bi learned\b/i,
      /\bmy (mistake|role|assumption)\b/i,
      /\bi should have\b/i,
      /\bi didn't do enough\b/i,
      /\bcustomer research\b/i,
      /\btakeaway\b/i,
      /\baccountability\b/i,
      /\bmy fault\b/i,
    ],
    red: [
      /\bthey didn't\b/i,
      /\bpeople just didn't\b/i,
      /\bbad luck\b/i,
      /\bnot fair\b/i,
      /\bnot my fault\b/i,
      /\bmy team was the problem\b/i,
      /\bmy boss was\b/i,
      /\binstructions were bad\b/i,
      /\bblame\b/i,
    ],
  },
  q4: {
    trait: "coachability",
    green: [
      /\btell me more\b/i,
      /\bask (them |you )?(for |to )?more\b/i,
      /\bwhat am i not seeing\b/i,
      /\bunderstand where\b/i,
      /\bcurious\b/i,
      /\blisten\b/i,
      /\bthank them\b/i,
      /\bexample\b/i,
      /\bprocess\b/i,
      /\bthink it through\b/i,
    ],
    red: [
      /\bdefensive\b/i,
      /\bi'd argue\b/i,
      /\bthey're wrong\b/i,
      /\bexplain why they're wrong\b/i,
      /\bignore\b/i,
      /\bnod and then ignore\b/i,
      /\bknow my business best\b/i,
      /\bwon't work for me\b/i,
      /\bjust say thanks and not\b/i,
    ],
  },
  "deep-q1": {
    trait: "coachability",
    green: [
      /\bopen\b/i,
      /\bcurious\b/i,
      /\bpartnership\b/i,
      /\bgrowth\b/i,
      /\bprepared\b/i,
    ],
    red: [
      /\bjust want answers\b/i,
      /\btell me what to do\b/i,
      /\bdon't have time\b/i,
    ],
  },
  "deep-q2": {
    trait: "coachability",
    green: [
      /\bdirect\b/i,
      /\bconstructive\b/i,
      /\bone-on-one\b/i,
      /\bprivate\b/i,
      /\bstraight\b/i,
      /\bcaring\b/i,
      /\bgentle\b/i,
    ],
    red: [
      /\bbrutal honesty\b/i,
      /\bnot sugarcoat\b/i,
      /\bdon't waste my time\b/i,
    ],
  },
  "accountability-probe": {
    trait: "accountability",
    green: [
      /\baccountability\b/i,
      /\bmy part\b/i,
      /\bi need\b/i,
      /\bstructure\b/i,
      /\bfollow[- ]?up\b/i,
    ],
    red: [
      /\bnot my fault\b/i,
      /\bthey should\b/i,
      /\bunfair\b/i,
    ],
  },
};

function scoreAnswerAgainstRubric(
  transcript: string,
  rubric: StepRubric
): { score: number; flag: string | null } {
  const text = transcript.trim();
  if (!text) {
    return { score: 50, flag: null };
  }

  const greenHits = rubric.green.filter((re) => re.test(text)).length;
  const redHits = rubric.red.filter((re) => re.test(text)).length;

  if (redHits > 0 && greenHits === 0) {
    return {
      score: 25,
      flag:
        rubric.trait === "accountability"
          ? "external_blame_signal"
          : "low_coachability_signal",
    };
  }

  if (greenHits > 0 && redHits === 0) {
    return { score: 85 + Math.min(greenHits * 3, 15), flag: null };
  }

  if (greenHits > redHits) {
    return { score: 70, flag: null };
  }

  if (redHits > greenHits) {
    return {
      score: 40,
      flag:
        rubric.trait === "accountability"
          ? "external_blame_signal"
          : "low_coachability_signal",
    };
  }

  return { score: 55, flag: null };
}

export function scoreAnswersWithRubrics(answers: IntakeAnswer[]): RubricScores {
  const accountabilityScores: number[] = [];
  const coachabilityScores: number[] = [];
  const flags = new Set<string>();

  for (const answer of answers) {
    if (answer.skipped || !answer.transcript.trim()) {
      continue;
    }

    const rubric = STEP_RUBRICS[answer.stepId];
    if (!rubric) {
      continue;
    }

    const { score, flag } = scoreAnswerAgainstRubric(answer.transcript, rubric);
    if (rubric.trait === "accountability") {
      accountabilityScores.push(score);
    } else {
      coachabilityScores.push(score);
    }
    if (flag) {
      flags.add(flag);
    }
  }

  return {
    accountability:
      accountabilityScores.length > 0
        ? Math.round(
            accountabilityScores.reduce((a, b) => a + b, 0) /
              accountabilityScores.length
          )
        : 55,
    coachability:
      coachabilityScores.length > 0
        ? Math.round(
            coachabilityScores.reduce((a, b) => a + b, 0) /
              coachabilityScores.length
          )
        : 55,
    flags: Array.from(flags),
  };
}
